import {
  IRecruiterDTO,
  IRecruiterSearchDTO,
  IRecruiterTeamNameAutoCompleteOption,
  IRecruiterSearchFilterDTO,
  IClientDTO,
  IJobTag,
  IClientRecruiterDTO,
  ISkill,
  getClientRecruiterNameSuggestions,
  getRecruitersFromWorkspace,
  IJobFieldSettings,
  ITeam,
  IJob,
  reopenJobForAssigningRecruiters,
  IJobSearchFilterDTO,
  ClientDTO,
  ClientContactDTO,
  findByName,
  ClientContactField,
  getParentList,
  getBusinessUnit,
  getBusinessUnitsByUserId,
  getMyTeams,
  getAllTeamsInWorkspace,
} from 'wisestep-api';
import { WsSelectObj } from 'wisestep-base';
import * as yup from 'yup';

import { success, failed } from '../../../utils/NotificationMessages';
import { INameAndId } from '../assign/types';
import { ListingSearchProps } from '../listing/components/jobSearch/ListingSearchProps';

export interface IJobIndustry {
  label: string;
  value: string;
}

export interface IJobOccupation {
  label: string;
  value: string;
}

export interface IJobCategory {
  label: string;
  value: string;
  occupations: IJobOccupation[];
}

const jobCategories: IJobCategory[] = [
  {
    label: 'Accounting/Finance/Insurance',
    value: '1',
    occupations: [
      { value: '11711', label: 'Accounts Payable/Receivable' },
      { value: '11712', label: 'Actuarial Analysis' },
      { value: '11724', label: 'Audit' },
      { value: '11730', label: 'Bookkeeping' },
      { value: '14866', label: 'Claims Review and Adjusting' },
      { value: '11750', label: 'Collections' },
      { value: '14867', label: 'Corporate Accounting' },
      { value: '11760', label: 'Corporate Finance' },
      { value: '11766', label: 'Credit Review/Analysis' },
      { value: '11803', label: 'Financial Analysis/Research/Reporting' },
      { value: '14868', label: 'Financial Control' },
      { value: '11804', label: 'Financial Planning/Advising' },
      { value: '11960', label: 'Financial Products Sales/Brokerage' },
      { value: '11817', label: 'Fund Accounting' },
      { value: '11893', label: 'General/Other: Accounting/Finance' },
      { value: '11843', label: 'Investment Management' },
      { value: '11927', label: 'Policy Underwriting' },
      { value: '14875', label: 'Real Estate Leasing/Acquisition' },
      { value: '11940', label: 'Real Estate Appraisal' },
      { value: '11952', label: 'Risk Management/Compliance' },
      { value: '11959', label: 'Securities Analysis/Research' },
      { value: '11981', label: 'Tax Accounting' },
      { value: '11982', label: 'Tax Assessment and Collections' },
    ],
  },
  {
    label: 'Administrative/Clerical',
    value: '2',
    occupations: [
      { value: '11713', label: 'Administrative Support' },
      { value: '14865', label: 'Claims Processing' },
      { value: '11770', label: 'Data Entry/Order Processing' },
      { value: '11797', label: 'Executive Support' },
      { value: '11802', label: 'Filing/Records Management' },
      { value: '11894', label: 'General/Other: Administrative/Clerical' },
      { value: '11888', label: 'Office Management' },
      { value: '14874', label: 'Property Management' },
      { value: '11942', label: 'Reception/Switchboard' },
      { value: '11958', label: 'Secretary/Executive Assistant' },
      { value: '14880', label: 'Transcription' },
    ],
  },

  {
    label: 'Agriculture, Forestry, & Fishing',
    value: '15581',
    occupations: [{ value: '15582', label: 'Agriculture' }],
  },
  {
    label: 'Banking/Real Estate/Mortgage Professionals',
    value: '558',
    occupations: [
      { value: '11726', label: 'Bank Teller' },
      { value: '15226', label: 'Credit Manager' },
      { value: '15232', label: 'Escrow Officer/Manager' },
      { value: '15228', label: 'Loan Officer/Originator' },
      { value: '15229', label: 'Mortgage Broker' },
      { value: '11939', label: 'Real Estate Agent/Broker' },
      { value: '11940', label: 'Real Estate Appraisal' },
      { value: '11941', label: 'Real Estate Law' },
      { value: '11975', label: 'Store/Branch Management' },
      { value: '15230', label: 'Title Officer/Closer' },
      { value: '15231', label: 'Underwriter' },
    ],
  },
  {
    label: 'Biotech/R&D/Science',
    value: '559',
    occupations: [
      { value: '11729', label: 'Biological/Chemical Research' },
      { value: '11749', label: 'Clinical Research' },
      { value: '11790', label: 'Environmental/Geological Testing & Analysis' },
      { value: '11912', label: 'General/Other: R&D/Science' },
      { value: '11865', label: 'Materials/Physical Research' },
      { value: '11866', label: 'Mathematical/Statistical Research' },
      { value: '11883', label: 'New Product R&D' },
      { value: '11921', label: 'Pharmaceutical Research' },
    ],
  },

  {
    label: 'Building Construction/Skilled Trades',
    value: '544',
    occupations: [
      { value: '11737', label: 'CAD/Drafting' },
      { value: '11743', label: 'Carpentry/Framing' },
      { value: '11755', label: 'Concrete and Masonry' },
      { value: '11783', label: 'Electrician' },
      { value: '11809', label: 'Flooring/Tiling/Painting/Wallpapering' },
      { value: '11896', label: 'General/Other: Construction/Skilled Trades' },
      { value: '11829', label: 'Heavy Equipment Operation' },
      { value: '11835', label: 'HVAC' },
      { value: '11845', label: 'Ironwork/Metal Fabrication' },
      { value: '11925', label: 'Plumbing/Pipefitting' },
      { value: '11953', label: 'Roofing' },
      { value: '11963', label: 'Sheetrock/Plastering' },
      { value: '11965', label: 'Site Superintendent' },
      { value: '11978', label: 'Surveying' },
    ],
  },

  {
    label: 'Business/Strategic Management',
    value: '3561',
    occupations: [
      { value: '11734', label: 'Business Analysis/Research' },
      { value: '11736', label: 'Business Unit Management' },
      { value: '11814', label: 'Franchise-Business Ownership' },
      { value: '11895', label: 'General/Other: Business/Strategic Management' },
      { value: '11830', label: 'Hospital/Clinic Administration' },
      { value: '14869', label: 'Hotel/Lodging Management' },
      { value: '11860', label: 'Managerial Consulting' },
      { value: '11875', label: 'Mergers and Acquisitions' },
      { value: '11929', label: 'President/Top Executive' },
      { value: '11936', label: 'Public Health Administration' },
      { value: '11947', label: 'Restaurant Management' },
      { value: '11955', label: 'School/College Administration' },
      { value: '11975', label: 'Store/Branch Management' },
      { value: '11976', label: 'Strategic Planning/Intelligence' },
      { value: '11990', label: 'Town/City Planning' },
    ],
  },

  {
    label: 'Creative/Design',
    value: '11454',
    occupations: [
      { value: '11714', label: 'Advertising Writing (Creative)' },
      { value: '11719', label: 'Architecture/Interior Design' },
      { value: '11752', label: 'Computer Animation & Multimedia' },
      { value: '11765', label: 'Creative Direction/Lead' },
      { value: '11800', label: 'Fashion & Accessories Design' },
      { value: '11897', label: 'General/Other: Creative/Design' },
      { value: '11821', label: 'Graphic Arts/Illustration' },
      { value: '11837', label: 'Industrial Design' },
      { value: '11923', label: 'Photography and Videography' },
      { value: '12005', label: 'Web/UI/UX Design' },
    ],
  },
  {
    label: 'Customer Support/Client Care',
    value: '545',
    occupations: [
      { value: '11710', label: 'Account Management (Non-Commissioned)' },
      { value: '11726', label: 'Bank Teller' },
      { value: '11738', label: 'Call Center' },
      { value: '11768', label: 'Customer Training' },
      { value: '11808', label: 'Flight Attendant' },
      { value: '11898', label: 'General/Other: Customer Support/Client Care' },
      { value: '11824', label: 'Hair Cutting/Styling' },
      { value: '11945', label: 'Reservations/Ticketing' },
      { value: '11948', label: 'Retail Customer Service' },
      { value: '11985', label: 'Technical Customer Service' },
    ],
  },

  {
    label: 'Editorial/Writing',
    value: '5623',
    occupations: [
      { value: '11775', label: 'Digital Content Development' },
      { value: '11778', label: 'Documentation/Technical Writing' },
      { value: '11781', label: 'Editing & Proofreading' },
      { value: '11899', label: 'General/Other: Editorial/Writing' },
      { value: '11850', label: 'Journalism' },
      { value: '11992', label: 'Translation/Interpretation' },
    ],
  },
  {
    label: 'Education/Training',
    value: '3',
    occupations: [
      { value: '11748', label: 'Classroom Teaching' },
      { value: '11756', label: 'Continuing/Adult' },
      { value: '11759', label: 'Corporate Development and Training' },
      { value: '11768', label: 'Customer Training' },
      { value: '11779', label: 'Early Childhood Care & Development' },
      { value: '11784', label: 'Elementary School' },
      { value: '11807', label: 'Fitness & Sports Training/Instruction' },
      { value: '11915', label: 'General/Other: Training/Instruction' },
      { value: '11851', label: 'Junior/High School' },
      { value: '11955', label: 'School/College Administration' },
      { value: '11971', label: 'Software/Web Training' },
      { value: '11972', label: 'Special Education' },
      { value: '11995', label: 'University' },
    ],
  },
  {
    label: 'Engineering',
    value: '4',
    occupations: [
      { value: '11715', label: 'Aeronautic/Avionic Engineering' },
      { value: '11728', label: 'Bio-Engineering' },
      { value: '11737', label: 'CAD/Drafting' },
      { value: '11746', label: 'Chemical Engineering' },
      { value: '11747', label: 'Civil & Structural Engineering' },
      { value: '11782', label: 'Electrical/Electronics Engineering' },
      { value: '11786', label: 'Energy/Nuclear Engineering' },
      { value: '11788', label: 'Environmental and Geological Engineering' },
      { value: '11900', label: 'General/Other: Engineering' },
      { value: '11838', label: 'Industrial/Manufacturing Engineering' },
      { value: '11867', label: 'Mechanical Engineering' },
      { value: '11881', label: 'Naval Architecture/Marine Engineering' },
      { value: '11951', label: 'RF/Wireless Engineering' },
      { value: '11980', label: 'Systems/Process Engineering' },
    ],
  },
  {
    label: 'Food Services/Hospitality',
    value: '13',
    occupations: [
      { value: '11810', label: 'Food & Beverage Serving' },
      { value: '11811', label: 'Food Preparation/Cooking' },
      { value: '11816', label: 'Front Desk/Reception' },
      { value: '11901', label: 'General/Other: Food Services' },
      { value: '11822', label: 'Guest Services/Concierge' },
      { value: '11823', label: 'Guide (Tour)' },
      { value: '11831', label: 'Host/Hostess' },
      { value: '11947', label: 'Restaurant Management' },
      { value: '12007', label: 'Wine Steward (Sommelier)' },
    ],
  },
  {
    label: 'Human Resources',
    value: '5',
    occupations: [
      { value: '14864', label: 'Academic Admissions and Advising' },
      { value: '11751', label: 'Compensation/Benefits Policy' },
      { value: '11759', label: 'Corporate Development and Training' },
      { value: '11777', label: 'Diversity Management/EEO/Compliance' },
      { value: '11902', label: 'General/Other: Human Resources' },
      { value: '11834', label: 'HR Systems Administration' },
      { value: '11918', label: 'Payroll and Benefits Administration' },
      { value: '11943', label: 'Recruiting/Sourcing ' },
    ],
  },
  {
    label: 'Installation/Maintenance/Repair',
    value: '553',
    occupations: [
      { value: '11753', label: 'Computer/Electronics/Telecomm Install/Maintain/Repair' },
      { value: '11783', label: 'Electrician' },
      { value: '11791', label: 'Equipment Install/Maintain/Repair' },
      { value: '11798', label: 'Facilities Management/Maintenance' },
      { value: '11903', label: 'General/Other: Installation/Maintenance/Repair' },
      { value: '11835', label: 'HVAC' },
      { value: '11849', label: 'Janitorial & Cleaning' },
      { value: '11854', label: 'Landscaping' },
      { value: '11858', label: 'Locksmith' },
      { value: '11889', label: 'Oil Rig & Pipeline Install/Maintain/Repair' },
      { value: '11925', label: 'Plumbing/Pipefitting' },
      { value: '12000', label: 'Vehicle Repair and Maintenance' },
      { value: '14881', label: 'Wire and Cable Install/Maintain/Repair' },
    ],
  },
  {
    label: 'IT/Software Development',
    value: '660',
    occupations: [
      { value: '11754', label: 'Computer/Network Security' },
      { value: '11772', label: 'Database Development/Administration' },
      { value: '11774', label: 'Desktop Service and Support' },
      { value: '11787', label: 'Enterprise Software Implementation & Consulting' },
      { value: '11904', label: 'General/Other: IT/Software Development' },
      { value: '11848', label: 'IT Project Management' },
      { value: '11882', label: 'Network and Server Administration' },
      { value: '11969', label: 'Software/System Architecture' },
      { value: '11970', label: 'Software/Web Development' },
      { value: '11979', label: 'Systems Analysis - IT' },
      { value: '11987', label: 'Telecommunications Administration/Management' },
      { value: '11996', label: 'Usability/Information Architecture' },
      { value: '12005', label: 'Web/UI/UX Design' },
    ],
  },

  {
    label: 'Legal',
    value: '7',
    occupations: [
      { value: '11722', label: 'Attorney' },
      { value: '11757', label: 'Contracts Administration' },
      { value: '11905', label: 'General/Other: Legal' },
      { value: '11852', label: 'Labor & Employment Law' },
      { value: '11916', label: 'Paralegal & Legal Secretary' },
      { value: '11917', label: 'Patent/IP Law' },
      { value: '11941', label: 'Real Estate Law' },
      { value: '11944', label: 'Regulatory/Compliance Law' },
      { value: '11983', label: 'Tax Law' },
    ],
  },

  {
    label: 'Logistics/Transportation',
    value: '5625',
    occupations: [
      { value: '11739', label: 'Car, Van and Bus Driving' },
      { value: '11742', label: 'Cargo and Baggage Handling' },
      { value: '11763', label: 'Cost Estimating' },
      { value: '11793', label: 'Equipment/Forklift/Crane Operation' },
      { value: '11906', label: 'General/Other: Logistics/Transportation' },
      { value: '11827', label: 'Hazardous Materials Handling' },
      { value: '11836', label: 'Import/Export Administration' },
      { value: '11842', label: 'Inventory Planning and Management' },
      { value: '11874', label: 'Merchandise Planning and Buying' },
      { value: '11876', label: 'Messenger/Courier' },
      { value: '11924', label: 'Piloting: Air and Marine' },
      { value: '11937', label: 'Purchasing' },
      { value: '11964', label: 'Shipping and Receiving/Warehousing' },
      { value: '11977', label: 'Supplier Management/Vendor Management' },
      { value: '11991', label: 'Train or Rail Operator' },
      { value: '11994', label: 'Truck Driving' },
      { value: '11998', label: 'Vehicle Dispatch, Routing and Scheduling' },
    ],
  },
  {
    label: 'Medical/Health',
    value: '3975',
    occupations: [
      { value: '11872', label: 'Dental Assistant/Hygienist' },
      { value: '11773', label: 'Dental Practitioner' },
      { value: '11785', label: 'EMT/Paramedic' },
      { value: '11908', label: 'General/Other: Medical/Health' },
      { value: '11820', label: 'Healthcare Aid' },
      { value: '11853', label: 'Laboratory/Pathology' },
      { value: '11938', label: 'Medical Imaging' },
      { value: '11870', label: 'Medical Practitioner' },
      { value: '11871', label: 'Medical Therapy/Rehab Services' },
      { value: '11873', label: 'Mental Health' },
      { value: '11885', label: 'Nursing' },
      { value: '11886', label: 'Nutrition and Diet' },
      { value: '11891', label: 'Optical' },
      { value: '11922', label: 'Pharmacy' },
      { value: '14873', label: 'Physician Assistant/Nurse Practitioner' },
      { value: '11936', label: 'Public Health Administration' },
      { value: '11967', label: 'Social Service' },
      { value: '11973', label: 'Sports Medicine' },
      { value: '12001', label: 'Veterinary/Animal Care' },
    ],
  },
  {
    label: 'Manufacturing/Production/Operations',
    value: '47',
    occupations: [
      { value: '11721', label: 'Assembly/Assembly Line' },
      { value: '11723', label: 'Audio/Video Broadcast & Postproduction' },
      { value: '11792', label: 'Equipment Operations' },
      { value: '11909', label: 'General/Other: Production/Operations' },
      { value: '11827', label: 'Hazardous Materials Handling' },
      { value: '14871', label: 'Laundry and Dry-Cleaning Operations' },
      { value: '11855', label: 'Layout, Prepress, Printing, & Binding Operations' },
      { value: '11859', label: 'Machining/CNC' },
      { value: '11877', label: 'Metal Fabrication and Welding' },
      { value: '11880', label: 'Moldmaking/Casting' },
      { value: '11890', label: 'Operations/Plant Management' },
      { value: '11933', label: 'Production/Operations Planning' },
      { value: '14878', label: 'Scientific/Technical Production' },
      { value: '11962', label: 'Sewing and Tailoring' },
      { value: '11987', label: 'Telecommunications Administration/Management' },
      { value: '12003', label: 'Waste Pick-up and Removal' },
    ],
  },
  {
    label: 'Marketing/Product',
    value: '9007',
    occupations: [
      { value: '11732', label: 'Brand/Product Marketing' },
      { value: '11758', label: 'Copy Writing/Editing' },
      { value: '11776', label: 'Direct Marketing (CRM)' },
      { value: '11795', label: 'Events/Promotional Marketing' },
      { value: '11907', label: 'General/Other: Marketing/Product' },
      { value: '11844', label: 'Investor and Public/Media Relations' },
      { value: '11862', label: 'Market Research' },
      { value: '11863', label: 'Marketing Communications' },
      { value: '11864', label: 'Marketing Production/Traffic' },
      { value: '11869', label: 'Media Planning and Buying' },
      { value: '11931', label: 'Product Management' },
      { value: '11988', label: 'Telemarketing' },
      { value: '12002', label: 'Visual/Display Merchandising' },
    ],
  },

  {
    label: 'Project/Program Management',
    value: '9008',
    occupations: [
      { value: '11794', label: 'Event Planning/Coordination' },
      { value: '11910', label: 'General/Other: Project/Program Management' },
      { value: '11848', label: 'IT Project Management' },
      { value: '11934', label: 'Program Management' },
      { value: '11935', label: 'Project Management' },
    ],
  },
  {
    label: 'Quality Assurance/Safety',
    value: '11455',
    occupations: [
      { value: '11733', label: 'Building/Construction Inspection' },
      { value: '11789', label: 'Environmental Protection/Conservation' },
      { value: '11813', label: 'Food Safety and Inspection' },
      { value: '11815', label: 'Fraud Investigation' },
      { value: '11911', label: 'General/Other: Quality Assurance/Safety' },
      { value: '11846', label: 'ISO Certification' },
      { value: '11887', label: 'Occupational Health and Safety' },
      { value: '11932', label: 'Production Quality Assurance' },
      { value: '11966', label: 'Six Sigma/Black Belt/TQM' },
      { value: '11968', label: 'Software Quality Assurance' },
      { value: '11999', label: 'Vehicle Inspection' },
    ],
  },

  {
    label: 'Sales/Retail/Business Development',
    value: '10',
    occupations: [
      { value: '11709', label: 'Account Management (Commissioned)' },
      { value: '11735', label: 'Business Development/New Accounts' },
      { value: '11801', label: 'Field Sales' },
      { value: '11960', label: 'Financial Products Sales/Brokerage' },
      { value: '11818', label: 'Fundraising' },
      { value: '11913', label: 'General/Other: Sales/Business Development' },
      { value: '11839', label: 'Insurance Agent/Broker' },
      { value: '11840', label: 'International Sales' },
      { value: '11868', label: 'Media and Advertising Sales' },
      { value: '11874', label: 'Merchandise Planning and Buying' },
      { value: '11939', label: 'Real Estate Agent/Broker' },
      { value: '11950', label: 'Retail/Counter Sales and Cashier' },
      { value: '11954', label: 'Sales Support/Assistance' },
      { value: '11975', label: 'Store/Branch Management' },
      { value: '11986', label: 'Technical Presales Support & Technical Sales' },
      { value: '11989', label: 'Telesales' },
      { value: '11993', label: 'Travel Agent/Ticket Sales' },
      { value: '12002', label: 'Visual/Display Merchandising' },
      { value: '12006', label: 'Wholesale/Reselling Sales' },
    ],
  },

  {
    label: 'Security/Protective Services',
    value: '555',
    occupations: [
      { value: '11718', label: 'Airport Security and Screening' },
      { value: '11762', label: 'Correctional Officer' },
      { value: '11769', label: 'Customs/Immigration' },
      { value: '11805', label: 'Firefighting and Rescue' },
      { value: '11914', label: 'General/Other: Security/Protective Services' },
      { value: '11879', label: 'Military Combat' },
      { value: '11926', label: 'Police-Law Enforcement' },
      { value: '11961', label: 'Security Guard' },
      { value: '14879', label: 'Security Intelligence & Analysis' },
      { value: '11974', label: 'Store Security/Loss Prevention' },
    ],
  },
  {
    label: 'Other',
    value: '11',
    occupations: [
      { value: '11892', label: 'Other' },
      { value: '12008', label: 'Work at Home' },
    ],
  },
];

const jobIndustries: IJobIndustry[] = [
  { value: '0', label: 'All' },
  { value: '28', label: 'Accounting and Auditing Services' },
  { value: '29', label: 'Advertising and PR Services' },
  { value: '6', label: 'Aerospace and Defense' },
  { value: '1', label: 'Agriculture/Forestry/Fishing' },
  { value: '30', label: 'Architectural and Design Services' },
  { value: '7', label: 'Automotive and Parts Mfg' },
  { value: '75', label: 'Automotive Sales and Repair Services' },
  { value: '23', label: 'Banking' },
  { value: '8', label: 'Biotechnology/Pharmaceuticals' },
  { value: '21', label: 'Broadcasting, Music, and Film' },
  { value: '76', label: 'Business Services - Other' },
  { value: '9', label: 'Chemicals/Petro-Chemicals' },
  { value: '14', label: 'Clothing and Textile Manufacturing' },
  { value: '77', label: 'Computer/IT Services' },
  { value: '32', label: 'Computer Hardware' },
  { value: '33', label: 'Computer Software' },
  { value: '10', label: 'Consumer Packaged Goods Manufacturing' },
  { value: '4', label: 'Construction - Industrial Facilities and Infrastructure' },
  { value: '78', label: 'Construction - Resvalueential & Commercial/Office' },
  { value: '38', label: 'Education' },
  { value: '11', label: 'Electronics, Components, and Semiconductor Mfg' },
  { value: '3', label: 'Energy and Utilities' },
  { value: '79', label: 'Engineering Services' },
  { value: '80', label: 'Entertainment Venues and Theaters' },
  { value: '81', label: 'Financial Services' },
  { value: '82', label: 'Food and Beverage Production' },
  { value: '50', label: 'Government and Military' },
  { value: '39', label: 'Healthcare Services' },
  { value: '44', label: 'Hotels and Lodging' },
  { value: '20', label: 'Internet Services' },
  { value: '24', label: 'Insurance' },
  { value: '34', label: 'Legal Services' },
  { value: '31', label: 'Management Consulting Services' },
  { value: '12', label: 'Manufacturing - Other' },
  { value: '83', label: 'Marine Mfg & Services' },
  { value: '84', label: 'Medical Devices and Supplies' },
  { value: '2', label: 'Metals and Minerals' },
  { value: '47', label: 'Nonprofit Charitable Organizations' },
  { value: '42', label: 'Performing and Fine Arts' },
  { value: '48', label: 'Personal and Household Services' },
  { value: '13', label: 'Printing and Publishing ' },
  { value: '26', label: 'Real Estate/Property Management' },
  { value: '27', label: 'Rental Services' },
  { value: '45', label: 'Restaurant/Food Services' },
  { value: '17', label: 'Retail' },
  { value: '74', label: 'Security and Surveillance' },
  { value: '43', label: 'Sports and Physical Recreation' },
  { value: '46', label: 'Staffing/Employment Agencies' },
  { value: '22', label: 'Telecommunications Services' },
  { value: '19', label: 'Transport and Storage - Materials' },
  { value: '18', label: 'Travel, Transportation and Tourism' },
  { value: '37', label: 'Waste Management' },
  { value: '15', label: 'Wholesale Trade/Import-Export' },
  { value: '85', label: 'Other/Not Classified' },
];
export const getAssignedTeams = (teams: ITeam[]) => teams?.map((team) => team.name ?? '');

export const extractTeamNameAndId = (team: ITeam[]) =>
  team?.map((team: ITeam) => (({ name: team.name, id: team.id } as INameAndId) ?? []));

export const getJobIndustries = () => jobIndustries;

export const getCategories = () => jobCategories;

export const getEmailPartiallyEncryptedForDisplay = (email: string): string => {
  return `******${email.slice(email.indexOf('@'))}`;
};

export const getParialNameAndEncryptedEmailForDisplay = (name: string, email: string): string => {
  return `${name} ${getEmailPartiallyEncryptedForDisplay(email)}`;
};

export const transformISkillsToStringArray = (skills: ISkill[]) =>
  skills?.map((item) => item.skill) ?? [];

export const transformClientRecruitersToEmailArray = (clientRecruiters: ClientContactField[]) =>
  clientRecruiters.map((recruiter) => recruiter.emailAddress);

export const transformIClientDTOsToStringArray = (clients: IClientDTO[]) =>
  clients?.map((client) => client.name) ?? [];

export const fetchNameAndEmailFromClientContact = (contacts: ClientContactDTO[]) =>
  contacts?.map(getAutocompleteLabelForClientContact) ?? [];

export const getAutocompleteLabelForClientContact = (contact: ClientContactDTO) =>
  `${contact?.firstName ? contact?.firstName + ' - ' : ''} ${contact.emailAddress}|${
    contact.markStatus ? 'Active' : 'Inactive'
  }`;

export const getAutocompleteLabelForClientContactInJobs = (contact: ClientContactDTO) =>
  `${contact?.firstName ? contact?.firstName + ' - ' : ''} ${contact.emailAddress}`;

export const transformIClientDTOsToNameAndIdArray = (clients: ClientDTO[]): INameAndId[] =>
  clients?.map((client) => ({ name: client.name, id: client.id })) ?? [];

export const showingParentClient = (clients: (ClientDTO & { parent: ClientDTO })[]) => {
  clients.sort((a, b) =>
    a.clientStatus?.isMarkInactive && !b.clientStatus?.isMarkInactive
      ? 1
      : !a.clientStatus?.isMarkInactive && b.clientStatus?.isMarkInactive
      ? -1
      : a.name.localeCompare(b.name)
  );
  return clients.map((client) => {
    if (client.parent && client.parent.name) {
      return `${client.parent.name} -> ${client.name}|${
        client.clientStatus?.isMarkInactive ? 'Inactive' : 'Active'
      }|${client.clientStatus?.clientStatusName}|${client.value}`;
    } else {
      return `${client.name}|${client.clientStatus?.isMarkInactive ? 'Inactive' : 'Active'}|${
        client.clientStatus?.clientStatusName
      }|${client.value}`;
    }
  });
};

export const getClientRecruiterSuggestionsBasedOnName = async (
  clientRecruiterNamePrefix: string,
  companyName: string
) => {
  const result = await getClientRecruiterNameSuggestions(clientRecruiterNamePrefix, companyName);
  return result;
};

export const getAssignedClientList = async () => {
  const result = await getParentList();
  const clientListData = new Map();
  result.data.forEach((data) => clientListData.set(data.id, data.name));
  return clientListData;
};

export const transformIClientRecruiterDTOsToKeyValArray = (
  clients: IClientRecruiterDTO[],
  keyName: string
) => clients?.map((item) => ({ [keyName]: item.encryptedNameEmailFieldForDisplay })) ?? [];

export const transformIClientRecruiterDTOsToStringArray = (
  clientRecruiters: IClientRecruiterDTO[]
) => clientRecruiters?.map((clientRec) => `${clientRec.name} - ${clientRec.email}`) ?? [];

export const transformIJobTagsToStringArray = (tags: IJobTag[]) =>
  tags?.map((item) => item.tag) ?? [];

export const getRecruiterFullName = (recruiter: IRecruiterDTO | IRecruiterSearchDTO) => {
  if (!recruiter.lastName && recruiter.firstName) {
    return recruiter.firstName;
  } else if (recruiter.lastName && recruiter.firstName) {
    return `${recruiter.firstName} ${recruiter.lastName}`;
  } else if (recruiter.lastName) {
    return `${recruiter.lastName}`;
  }

  return recruiter.email;
};
export const getOnDemandWorkspaceMemberAutocompleteOptions = async (queryText: string) => {
  const query: IRecruiterSearchFilterDTO = {
    size: 10,
    sort: ['createdOn,DESC'],
    recruiterEmailPhrase: queryText,
    recruiterFirstNamePhrase: queryText,
    recruiterLastNamePhrase: queryText,
    page: 0,
    query: '',
  };

  const result = await getRecruitersFromWorkspace(query);
  const recruiters: IRecruiterSearchDTO[] = result.data;

  const finalResult: IRecruiterTeamNameAutoCompleteOption[] =
    recruiters?.map(function (recruiter) {
      const name = getRecruiterFullName(recruiter);
      const text = `${name} (${recruiter.email})`;

      return {
        text: text,
        email: recruiter.email,
        id: recruiter.id,
        name: name,
      };
    }) || [];

  return finalResult as IRecruiterTeamNameAutoCompleteOption[];
};

export const transformIRecruiterTeamNameOptionsToStringArray = (
  teamNameOptions: IRecruiterTeamNameAutoCompleteOption[]
): string[] => teamNameOptions?.map((item) => item.text) ?? [];

export const transformIRecruiterTeamNameOptionsToEmailArray = (
  teamNameOptions: IRecruiterTeamNameAutoCompleteOption[]
): string[] => teamNameOptions?.map((item) => item.email) ?? [];

export const getAllTeamIdsOfRecruiter = (recruiter: IRecruiterDTO): (string | undefined)[] => [
  ...(recruiter.ownsTeams?.map((item) => item.id) ?? []),
  ...(recruiter.belongsToTeams?.map((item) => item.id) ?? []),
];

interface validationField {
  [key: string]: any;
}

export const jobValidation = (jobFieldSettings?: IJobFieldSettings) => {
  const validations: validationField = {};

  if (!jobFieldSettings) {
    return yup.object().shape(validations);
  }

  if (jobFieldSettings.title?.mandatory) {
    validations['title'] = yup.string().required('Title is required');
  }
  if (jobFieldSettings.jobCode?.mandatory) {
    validations['jobCode'] = yup.string().required('Job Code is required');
  }
  if (jobFieldSettings.clientJobCode?.mandatory) {
    validations['clientJobCode'] = yup.string().required('Client Job Code is required');
  }
  if (jobFieldSettings.jobSource?.mandatory) {
    validations['jobSource'] = yup.string().required('Job Source is required');
  }
  if (jobFieldSettings.jobType?.mandatory) {
    validations['jobType'] = yup.string().required('Job Type is required');
  }
  if (jobFieldSettings.description?.mandatory) {
    validations['description'] = yup.string().required('Description is required');
  }
  if (jobFieldSettings.numberOfPositions?.mandatory) {
    validations['numberOfPositions'] = yup.string().required('Number of Positions is required');
  }
  if (jobFieldSettings.jobIndustryName?.mandatory) {
    validations['jobIndustryName'] = yup.string().required('Job Industry is required');
  }
  if (jobFieldSettings.companyName?.mandatory) {
    validations['companyName'] = yup.string().required('Client Name is required');
  }
  if (jobFieldSettings.locations?.mandatory) {
    validations['locations'] = yup.array().min(1, 'Location is required');
  }
  if (jobFieldSettings.compensation?.mandatory) {
    validations['salaryCurrency'] = yup.string().required('Compensation is required');
    validations['minimumSalary'] = yup.number().min(1, 'Minimum Salary is required');
    validations['maximumSalary'] = yup.number().required('maximum salary is required');
    validations['salaryTimeSpan'] = yup.string().required('Salary time span is required');
  }
  if (jobFieldSettings.jobStatusType?.mandatory) {
    validations['jobStatusType'] = yup.string().required('Job Status Type is required');
  }
  if (jobFieldSettings.workplacePreference?.mandatory) {
    validations['workplacePreference'] = yup.string().required('Workplace Preference is required');
  }
  if (jobFieldSettings.businessUnit?.mandatory) {
    validations['businessUnit'] = yup.string().required('Business Unit is required');
  }
  if (jobFieldSettings.skills?.mandatory) {
    validations['skills'] = yup.array().required('Skills is required');
  }
  if (jobFieldSettings.clientRecruiterName?.mandatory) {
    validations['clientRecruiter'] = yup.string().required('Client Recruiter Name is required');
  }
  if (jobFieldSettings.tags?.mandatory) {
    validations['tags'] = yup.array().required('Tags is required');
  }
  if (jobFieldSettings.clientJobCode?.mandatory) {
    validations['clientJobCode'] = yup.string().required('Client Job Code is required');
  }
  if (jobFieldSettings.accountManagerName?.mandatory) {
    validations['accountManagerName'] = yup.string().required('Account Manager Name is required');
  }
  if (jobFieldSettings.jobOccupation?.mandatory) {
    validations['jobOccupation'] = yup.string().required('Job Occupation is required');
  }
  if (jobFieldSettings.insight?.mandatory) {
    validations['insights'] = yup.array().required('Job Insight is required');
  }
  if (jobFieldSettings.jobCategory?.mandatory) {
    validations['jobCategory'] = yup.string().required('Job Category is required');
  }
  if (jobFieldSettings.workAuthorization?.mandatory) {
    validations['workAuthorization'] = yup.string().required('Work Authorization is required');
  }
  if (jobFieldSettings.url?.mandatory) {
    validations['url'] = yup.string().required('URL is required').url('Please enter a valid URL');
  }
  if (jobFieldSettings.otherBenefitDetails?.mandatory) {
    validations['otherBenefitDetails'] = yup.string().required('Other Benefit Details is required');
  }
  if (jobFieldSettings.visaRequirements?.mandatory) {
    validations['visaRequirements'] = yup.string().required('Visa Requirements is required');
  }
  if (jobFieldSettings.qualifications?.mandatory) {
    validations['qualifications'] = yup.array().required('Qualifications is required');
  }
  if (jobFieldSettings.educationRequirements?.mandatory) {
    validations['educationRequirements'] = yup
      .mixed()
      .required('Education Requirements is required');
  }

  if (jobFieldSettings.experienceRequirements?.mandatory) {
    validations['experienceRequirements'] = yup
      .mixed()
      .required('Experience Requirements is required');
  }
  if (jobFieldSettings.taxTerms?.mandatory) {
    validations['taxTerms'] = yup.string().required('Tax Terms is required');
  }
  if (jobFieldSettings.hiringFor?.mandatory) {
    validations['hiringFor'] = yup.string().required('Hiring For is required');
  }
  if (jobFieldSettings.workDuration?.mandatory) {
    validations['workDuration'] = yup.string().required('Work Duration is required');
  }
  if (jobFieldSettings.defaultCurrency?.mandatory) {
    validations['defaultCurrency'] = yup.string().required('Default Currency is required');
  }
  if (jobFieldSettings.feeArrangement?.mandatory) {
    validations['feeArrangementId'] = yup.string().required('Fee Arrangement is required');
  }
  if (jobFieldSettings.clientBillRateNew?.mandatory) {
    validations['clientBillRateNew'] = yup.string().required('Client Bill Rate is required');
  }
  if (jobFieldSettings.clientBillRate?.mandatory) {
    validations['clientBillRate'] = yup.string().required('Client Bill Rate is required');
  }
  if (jobFieldSettings.payRateExpenses?.mandatory) {
    validations['payRateExpenses'] = yup.string().required('Pay Rate Expenses is required');
  }
  if (jobFieldSettings.submissionFormTemplete?.mandatory) {
    validations['submissionFormTemplete'] = yup
      .string()
      .required('Submission form Templete is required');
  }
  const validationSchema = yup.object().shape(validations);
  return validationSchema;
};

export const formatJobSalary = (job: IJob): string => {
  const taxTerms = job.taxTerms ? `On ${job.taxTerms}` : '';
  const salaryString = `${job.salaryCurrency ?? ''} ${job.minimumSalary ?? ''} ${
    job.minimumSalary && job.maximumSalary ? ' - ' : ''
  } ${job.maximumSalary ?? ''} ${job.salaryTimeSpan ?? ''} ${taxTerms}`;

  if (salaryString.trim().length == 0) return 'Not Available';

  return salaryString;
};

export const formatJobLocations = (job: IJob): string => {
  if (!job.locations || job.locations.length === 0) {
    return 'Not Available';
  }
  const locationString = job.locations
    .map((location) => {
      const city = location.city ? location.city : '';
      const state = location.state ? location.state : '';
      const country = location.country ? location.country : '';
      return [city, state, country].filter(Boolean).join(', ');
    })
    .filter((loc) => loc.trim().length > 0)
    .join(' | ');

  return locationString.trim().length > 0 ? locationString : 'Not Available';
};

export const getAllTeamsOfCurrentUser = async () => {
  const teamBelongsto = await getMyTeams();
  const teamOwns = await getAllTeamsInWorkspace();
  const currentUserTeams = [...(teamBelongsto?.data ?? []), ...(teamOwns?.data ?? [])];
  return currentUserTeams as ITeam[];
};

export const getMaskedPhoneNumber = (phone: string) => `****${phone.slice(phone.lastIndexOf('-'))}`;

export const reOpenJob = async (jobId: string) => {
  if (jobId) {
    try {
      await reopenJobForAssigningRecruiters(jobId);
      return success('Job reopened successfully.');
    } catch {
      return failed('Failed to Reopen the Job. Please try again.');
    }
  } else {
    return failed('Something went wrong!');
  }
};

export const setDefaultSearchFilterJobListing = (
  query: IJobSearchFilterDTO
): ListingSearchProps => {
  return {
    query: query.query ?? null,
    accountMangers: query.postedByAccountManagerEmails ?? [],
    fromDate: query.postedFromDate ? new Date(query.postedFromDate) : null,
    toDate: query.postedToDate ? new Date(query.postedToDate) : null,
    minSubmissions: query.submissionsCountRange?.from ?? null,
    maxSubmissions: query.submissionsCountRange?.to ?? null,
  };
};

export const formatSalaryDetails = (job: IJob, taxTerms: string): string => {
  let label = ' Not Available';
  if (job.salaryCurrency) {
    if (job.minimumSalary && job.maximumSalary) {
      label = `${job.salaryCurrency} ${job.minimumSalary} - ${job.maximumSalary} ${job.salaryTimeSpan} ${taxTerms}`;
    } else if (job.maximumSalary) {
      label = `Max ${job.salaryCurrency} ${job.maximumSalary} ${job.salaryTimeSpan} ${taxTerms}`;
    } else if (job.minimumSalary) {
      label = `Min ${job.salaryCurrency} ${job.minimumSalary} ${job.salaryTimeSpan} ${taxTerms}`;
    }
  }
  return label;
};

export const getClientIdByClientName = async (clientName) => {
  try {
    const client = await findByName(clientName);
    if (client.data.data) return client.data?.data?.id;
  } catch {
    console.log('Something went wrong in fetching clientId by ClientName');
  }
};

export const fetchBusinessUnits = async (setJobEntity, setBusinessUnits) => {
  const tempArray: WsSelectObj[] = [];
  const businessUnitsList = await getBusinessUnitsByUserId();
  if (businessUnitsList.data.length === 1) {
    setJobEntity((prevJob) => ({ ...prevJob, businessUnit: businessUnitsList.data[0].id }));
  }
  if (businessUnitsList.data.length === 0) {
    tempArray.push({ label: 'Not Available', value: 'notAvailable' });
    setJobEntity((prevJob) => ({ ...prevJob, businessUnit: 'notAvailable' }));
  }
  businessUnitsList.data.map((businessUnit) => {
    tempArray.push({ label: businessUnit.name, value: businessUnit.id });
  });
  setBusinessUnits(tempArray);
};

export const getBUName = async (businessUnitIds) => {
  const businessUnits = await getBusinessUnit();
  return businessUnitIds.map((id) => {
    const businessUnit = businessUnits.data.find((unit) => unit.id === id);
    return businessUnit ? { id: businessUnit.id, name: businessUnit.name } : null;
  });
};

export type ChipStatus = 'normal' | 'highlighted';

export interface ISkillOption {
  label: string;
  status?: ChipStatus;
}

export const mapToNormalSkillOptions = (skills: string[]): ISkillOption[] => {
  if (!skills || skills.length === 0) {
    return [];
  }

  return skills.map((skill) => ({
    label: skill,
    status: 'normal',
  }));
};

export const newSkillOptions = (
  existingSkills: string[] = [],
  newSkills: string[] = []
): ISkillOption[] => {
  const existingSet = new Set(existingSkills);
  return [
    ...existingSkills.map((s) => ({ label: s, status: 'normal' as ChipStatus })),
    ...newSkills
      .filter((s) => !existingSet.has(s))
      .map((s) => ({ label: s, status: 'highlighted' as ChipStatus })),
  ];
};

export const calculateProgress = (description: string, skills: string[]): number => {
  let progress = 0;
  const wordCount = description ? description.trim().split(/\s+/).length : 0;
  const descProgress = Math.min((wordCount / 300) * 50, 50);
  progress += descProgress;
  const skillCount = skills?.length || 0;
  const skillProgress = Math.min((skillCount / 10) * 50, 50);
  progress += skillProgress;
  return Math.round(progress);
};
